import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUiStore } from '../../store/uiSlice';
import { clsx } from 'clsx';

export const Header: React.FC = () => {
  const { isMobileMenuOpen, setMobileMenuOpen } = useUiStore();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const badgeRef = useRef<HTMLDivElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [ecosystemOpen, setEcosystemOpen] = useState(false);
  const [ecosystemMobileOpen, setEcosystemMobileOpen] = useState(false);

  /* ── Scroll Detection ──────────────────────────────────────── */
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    handleScroll(); // check initial state
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ── CertiK Badge ─────────────────────────────────────────── */
  useEffect(() => {
    const badge = document.createElement('div');
    badge.className = 'certik-emblem';
    badge.setAttribute('data-id', '432b018d');
    badge.innerHTML = `
      <a href="https://skynet.certik.com/projects/idngold?utm_source=SkyEmblem&utm_campaign=idngold&utm_medium=link" target="_blank" rel="noopener noreferrer">
           &nbsp;&nbsp;Project  Certik&nbsp;&nbsp;
      </a>
    `;
    badgeRef.current = badge;

    const desktopContainer = document.getElementById('certik-desktop-container');
    if (desktopContainer) desktopContainer.appendChild(badge);

    const script = document.createElement('script');
    script.src = 'https://emblem.certik-assets.com/script?pid=idngold&vid=432b018d';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) document.body.removeChild(script);
      if (badge && badge.parentNode) badge.parentNode.removeChild(badge);
    };
  }, []);

  /* ── CertiK Reparenting (mobile/desktop) ─────────────────── */
  useEffect(() => {
    const badge = badgeRef.current;
    const desktopContainer = document.getElementById('certik-desktop-container');
    const mobileContainer = document.getElementById('certik-mobile-container');
    if (!badge || !desktopContainer || !mobileContainer) return;
    if (isMobileMenuOpen) {
      mobileContainer.appendChild(badge);
    } else {
      desktopContainer.appendChild(badge);
    }
  }, [isMobileMenuOpen]);

  /* ── Close mobile menu on route change ───────────────────── */
  useEffect(() => {
    setMobileMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  /* ── Language Toggle ─────────────────────────────────────── */
  const toggleLanguage = async () => {
    const currentLang = i18n.language || 'en';
    const newLang = currentLang.startsWith('en') ? 'id' : 'en';
    try {
      await i18n.changeLanguage(newLang);
      localStorage.setItem('i18nextLng', newLang);
      window.dispatchEvent(new Event('languageChanged'));
    } catch (error) {
      console.error('Failed to change language:', error);
    }
  };

  const isActive = (path: string) => location.pathname === path;
  const currentLang = i18n.language || 'en';
  const isEN = currentLang.startsWith('en');

  return (
    <header className={clsx('site-header', scrolled && 'scrolled')}>
      {/* ── Main Nav Bar ──────────────────────────────────────── */}
      <div className="container nav">
        {/* Logo */}
        <Link to="/" className="brand">
          <img
            src="/idngold_icon.webp"
            alt="IDNGold Icon"
            className="brand-icon"
          />
          <img
            src="/idngold_text.webp"
            alt="IDNGold"
            className="brand-text"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="nav-links">
          <Link to="/" className={clsx(isActive('/') && 'active')}>
            {t('nav.home')}
          </Link>
          <Link to="/buy" className={clsx(isActive('/buy') && 'active')}>
            {t('nav.buy')}
          </Link>
          <div
            className="relative flex items-center"
            onMouseEnter={() => setEcosystemOpen(true)}
            onMouseLeave={() => setEcosystemOpen(false)}
          >
            <button
              className={clsx(isActive('/redemption') && 'active')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', padding: 0, font: 'inherit', color: 'inherit' }}
            >
              {t('nav.ecosystem')}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.7, marginTop: '1px', transition: 'transform 0.2s', transform: ecosystemOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {ecosystemOpen && (
              <div
                className="absolute top-full left-0 pt-2"
                style={{ minWidth: '190px', zIndex: 50 }}
              >
                <div
                  className="py-2 rounded-xl"
                  style={{ background: 'rgba(20,18,14,0.97)', border: '1px solid rgba(245,158,11,0.2)', backdropFilter: 'blur(12px)' }}
                >
                <a
                  href="https://tools.idngold.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors hover:text-gold"
                  style={{ color: '#A89880' }}
                >
                  {t('nav.ecosystemTools')}
                  <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
                <Link
                  to="/redemption"
                  className="flex items-center px-4 py-2.5 text-sm font-medium transition-colors hover:text-gold"
                  style={{ color: isActive('/redemption') ? '#F59E0B' : '#A89880' }}
                >
                  {t('nav.ecosystemRedemption')}
                </Link>
                </div>
              </div>
            )}
          </div>
          <a
            href={`https://news.${window.location.hostname.replace('www.', '')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('nav.news')}
          </a>
          <Link to="/roadmap" className={clsx(isActive('/roadmap') && 'active')}>
            Roadmap
          </Link>
          <Link to="/whitepaper" className={clsx(isActive('/whitepaper') && 'active')}>
            Whitepaper
          </Link>
          <Link to="/tokenomics" className={clsx(isActive('/tokenomics') && 'active')}>
            {t('nav.tokenomics')}
          </Link>

          {/* CertiK Emblem — Desktop */}
          <div
            id="certik-desktop-container"
            className="flex items-center transform scale-125 origin-right ml-12 mr-6"
          />

          {/* Language Toggle */}
          <button
            onClick={toggleLanguage}
            className="flex items-center justify-center w-9 h-9 rounded-lg transition-all duration-200"
            style={{
              background: 'rgba(245,158,11,0.07)',
              border: '1px solid rgba(245,158,11,0.15)',
            }}
            title={isEN ? 'Switch to Bahasa Indonesia' : 'Switch to English'}
          >
            <img
              src={isEN ? '/flag-id.svg' : '/flag-us.svg'}
              alt={isEN ? 'Indonesia' : 'English'}
              className="w-5 h-5 rounded"
            />
          </button>
        </nav>

        {/* Mobile Menu Toggle */}
        <button
          className="nav-toggle btn btn-ghost"
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle navigation"
          aria-expanded={isMobileMenuOpen}
        >
          {/* Animated hamburger → X */}
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ transition: 'transform 0.25s ease' }}
          >
            {isMobileMenuOpen ? (
              /* X icon */
              <path
                d="M6 6L18 18M6 18L18 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            ) : (
              /* Hamburger icon */
              <path
                d="M3 12H21M3 6H21M3 18H21"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </svg>
        </button>
      </div>

      {/* ── Mobile Navigation Drawer ─────────────────────────── */}
      <div
        className="container"
        style={{
          overflow: 'hidden',
          maxHeight: isMobileMenuOpen ? '600px' : '0',
          transition: 'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <div
          className="md:hidden py-4"
          style={{
            borderTop: '1px solid rgba(245,158,11,0.12)',
            opacity: isMobileMenuOpen ? 1 : 0,
            transform: isMobileMenuOpen ? 'translateY(0)' : 'translateY(-8px)',
            transition: 'opacity 0.3s ease 0.05s, transform 0.3s ease 0.05s',
          }}
        >
          <nav className="flex flex-col space-y-1">
            {/* Mobile nav links — before Ekosistem */}
            {[
              { to: '/', label: t('nav.home') },
              { to: '/buy', label: t('nav.buy') },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={clsx(
                  'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive(to) ? 'text-gold bg-gold/10' : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                )}
                onClick={() => setMobileMenuOpen(false)}
                style={isActive(to) ? { color: '#F59E0B', background: 'rgba(245,158,11,0.10)' } : {}}
              >
                {isActive(to) && <span className="mr-2 w-1 h-4 rounded-full inline-block" style={{ background: '#F59E0B' }} />}
                {label}
              </Link>
            ))}

            {/* Ekosistem toggle */}
            <div>
              <button
                onClick={() => setEcosystemMobileOpen(!ecosystemMobileOpen)}
                className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                style={{ color: isActive('/redemption') ? '#F59E0B' : '#A89880' }}
              >
                <span className="flex items-center gap-2">
                  {isActive('/redemption') && <span className="w-1 h-4 rounded-full inline-block" style={{ background: '#F59E0B' }} />}
                  {t('nav.ecosystem')}
                </span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.2s', transform: ecosystemMobileOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {ecosystemMobileOpen && (
                <div className="ml-3 mt-1 flex flex-col gap-0.5 pl-3" style={{ borderLeft: '2px solid rgba(245,158,11,0.2)' }}>
                  <a
                    href="https://tools.idngold.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:bg-white/5"
                    style={{ color: '#A89880' }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.ecosystemTools')}
                    <svg className="w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                  <Link
                    to="/redemption"
                    className={clsx('flex items-center px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200', isActive('/redemption') ? 'text-gold' : 'hover:bg-white/5')}
                    style={{ color: isActive('/redemption') ? '#F59E0B' : '#A89880' }}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {t('nav.ecosystemRedemption')}
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile nav links — after Ekosistem */}
            {[
              { to: '/roadmap', label: 'Roadmap' },
              { to: '/whitepaper', label: 'Whitepaper' },
              { to: '/tokenomics', label: t('nav.tokenomics') },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className={clsx(
                  'flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200',
                  isActive(to) ? 'text-gold bg-gold/10' : 'text-text-secondary hover:text-text-primary hover:bg-white/5'
                )}
                onClick={() => setMobileMenuOpen(false)}
                style={isActive(to) ? { color: '#F59E0B', background: 'rgba(245,158,11,0.10)' } : {}}
              >
                {isActive(to) && <span className="mr-2 w-1 h-4 rounded-full inline-block" style={{ background: '#F59E0B' }} />}
                {label}
              </Link>
            ))}

            {/* News external link */}
            <a
              href={`https://news.${window.location.hostname.replace('www.', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200"
              style={{ color: '#A89880' }}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('nav.news')}
              <svg className="ml-1.5 w-3 h-3 opacity-60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>

            {/* Language Toggle — Mobile */}
            <div className="pt-3 mt-2" style={{ borderTop: '1px solid rgba(245,158,11,0.08)' }}>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-3 px-3 py-2.5 w-full rounded-lg text-sm font-medium transition-all duration-200"
                style={{ color: '#A89880' }}
              >
                <img
                  src={isEN ? '/flag-id.svg' : '/flag-us.svg'}
                  alt={isEN ? 'Indonesia' : 'English'}
                  className="w-5 h-5 rounded"
                />
                <span>{isEN ? 'Bahasa Indonesia' : 'English'}</span>
              </button>
            </div>

            {/* CertiK Emblem — Mobile */}
            <div
              id="certik-mobile-container"
              className="px-3 py-3 flex items-center justify-start transform scale-125 origin-left ml-2"
            />
          </nav>
        </div>
      </div>
    </header>
  );
};
